/**
 * Widget notification chime + a per-visitor mute preference (localStorage). A short Web Audio
 * beep plays when an agent/bot message arrives while the visitor has the widget open, unless muted
 * via the header menu. Feature-detected so an unsupported/SSR environment never throws.
 */

const MUTE_KEY = 'lc.widget.muted';
/** Pre-rename key, read-only: keeps a visitor's existing mute preference after the rename. */
const LEGACY_MUTE_KEY = 'flozic.widget.muted';

// Org-level sound gate (from `OrgTheme.messageSound`). When the org disables sound the widget
// never chimes and hides the per-visitor toggle; when enabled (default), the per-visitor mute
// pref still applies. Defaults to enabled so a never-configured org keeps chiming.
let orgSoundEnabled = true;

/** Set the org-level sound gate (called once the widget config loads). */
export function setSoundEnabled(enabled: boolean): void {
  orgSoundEnabled = enabled;
}

/** Whether the org allows notification sound at all. */
export function soundEnabled(): boolean {
  return orgSoundEnabled;
}

export function isMuted(): boolean {
  // Org gate off → effectively muted regardless of the per-visitor pref.
  if (!orgSoundEnabled) return true;
  try {
    return (localStorage.getItem(MUTE_KEY) ?? localStorage.getItem(LEGACY_MUTE_KEY)) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Play a short ~660Hz sine beep unless muted. */
export function playChime(): void {
  if (typeof window === 'undefined' || isMuted()) return;
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 660;
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
    osc.onended = () => {
      void ctx.close().catch(() => {});
    };
  } catch {
    /* autoplay policy / audio unavailable — silently skip */
  }
}
